// hooks/CustomHooks/useChatMessages.ts
import { useCallback, useEffect, useRef } from 'react';
import { create } from 'zustand';
import { nanoid } from 'nanoid/non-secure';
import { ChatMessage, ChatState } from 'pages/MindUp/Sungnam/AskToAI/types';
import { AI_CONSTANTS, MESSAGES } from 'pages/MindUp/Sungnam/AskToAI/constants';

type ChatMessageWithQuota = ChatMessage & { countsAsQuestion?: boolean };


export const useChatStore = create<
    ChatState & {
        setAll: (msgs: ChatMessageWithQuota[]) => void;
    }
>((set) => ({
    messages: [],
    addMessage: (m) => {
        const msg: ChatMessageWithQuota = {
            id: nanoid(),
            chatTimestamp: (m as ChatMessageWithQuota).chatTimestamp ?? Date.now(),
            countsAsQuestion: (m as ChatMessageWithQuota).countsAsQuestion ?? true,
            ...(m as any),
        };
        set((s) => ({ messages: [...(s.messages as ChatMessageWithQuota[]), msg] }));
        return msg as ChatMessage;
    },
    prependOlder: (older) =>
        set((s) => ({
            messages: [...(older as ChatMessageWithQuota[]), ...(s.messages as ChatMessageWithQuota[])],
        })),
    clear: () => set({ messages: [] }),
    setAll: (msgs) => set({ messages: msgs }),
}));

/**
 * 서버 데이터 항상 최신 동기화
 * - 매번 서버의 initialMessages를 최신으로 사용
 * - 안내 메시지 + 서버 이력 항상 표시
 */
export const useChatMessages = (
    initialMessages: ChatMessage[] | undefined,
    chatId: string,
    introText = MESSAGES.FIRST_MESSAGE
) => {
    const { messages, addMessage, setAll } = useChatStore();
    const hydratedRef = useRef(false);

    // 카운트 계산
    const userQuestionCount = (messages as ChatMessageWithQuota[]).filter((m) => m.role === 'user' && m.countsAsQuestion !== false).length;

    const remainingQuestions = Math.max(0, AI_CONSTANTS.MAX_QUESTIONS - userQuestionCount);
    const isQuestionLimitReached = userQuestionCount >= AI_CONSTANTS.MAX_QUESTIONS;

    // --- 1) 항상 서버 데이터로 초기화 ---
    useEffect(() => {
        if (hydratedRef.current) return;
        hydratedRef.current = true;

        // 항상 안내 메시지 + 서버 이력으로 시작
        const introMsg: ChatMessageWithQuota = {
            role: 'assistant',
            text: introText,
            chatTimestamp: Date.now() - 1000000, // 안내 메시지가 항상 맨 위에 오도록
            countsAsQuestion: false,
        };

        const serverMsgs = (initialMessages ?? []).map((m) => ({
            ...m,
            countsAsQuestion: m.role === 'user' ? true : false,
        })) as ChatMessageWithQuota[];

        // 안내를 가장 위에, 그 다음 서버 이력
        setAll([introMsg, ...serverMsgs]);
    }, [chatId, initialMessages, introText, setAll]);

    // --- 2) localStorage 사용 안 함 (항상 서버 데이터 최신 유지) ---

    // --- 3) API: 유저/어시스턴트 메시지 추가 ---
    const addUserMessage = useCallback(
        (text: string, opts?: { countsAsQuestion?: boolean }) => {
            return addMessage({
                role: 'user',
                text,
                countsAsQuestion: opts?.countsAsQuestion ?? true,
            } as any);
        },
        [addMessage]
    );

    const addAssistantMessage = useCallback(
        (text: string, isLastQuestion = false, opts?: { countsAsQuestion?: boolean }) => {
            const finalText = isLastQuestion ? `${text}${MESSAGES.LIMIT_REACHED_SUFFIX}` : text;
            return addMessage({
                role: 'assistant',
                text: finalText,
                countsAsQuestion: opts?.countsAsQuestion ?? false,
            } as any);
        },
        [addMessage]
    );

    return {
        messages,
        userQuestionCount,
        remainingQuestions,
        isQuestionLimitReached,
        addUserMessage,
        addAssistantMessage,
    };
};
